import React, { useState, useEffect } from 'react';
import AppNav from './AppNav';
import { useRipple } from '../hooks/useRipple';
import './styles/clickEffects.css';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const SECTIONS = [
  { id: 'intellectual-property', label: 'Intellectual Property', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
  )},
  { id: 'user-conduct', label: 'User Conduct', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  )},
  { id: 'limitation-of-liability', label: 'Limitation of Liability', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  )},
  { id: 'data-usage', label: 'Data Usage', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
  )},
  { id: 'governing-law', label: 'Governing Law', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22V4M4 8l8-4 8 4M4 16l8 4 8-4M4 12l8 4 8-4"/></svg>
  )},
  { id: 'modifications', label: 'Modifications', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
  )},
  { id: 'contact', label: 'Contact', icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
  )},
];

export default function TermsPage({
  priceMode = 'student', onModeToggle, onNavigate,
  user = null, onUserChange,
}) {
  const isMobile = useIsMobile();
  const [activeSection, setActiveSection] = useState('intellectual-property');
  const navH = isMobile ? 60 : 72;
  const triggerRipple = useRipple();

  // Update active section on scroll
  useEffect(() => {
    const handler = () => {
      const offsets = SECTIONS.map(s => {
        const el = document.getElementById(s.id);
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: Math.abs(el.getBoundingClientRect().top - 120) };
      });
      const closest = offsets.sort((a, b) => a.top - b.top)[0];
      if (closest) setActiveSection(closest.id);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const SectionHeader = ({ num, title, icon }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: 'linear-gradient(135deg, var(--green-900,#0a2e14) 0%, #1a4731 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#d4a017', flexShrink: 0,
        boxShadow: '0 4px 12px rgba(10,46,20,0.2)',
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#d4a017', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 2 }}>
          Section {num}
        </div>
        <h2 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#001805', margin: 0, letterSpacing: '-0.02em' }}>
          {title}
        </h2>
      </div>
    </div>
  );

  const Card = ({ children, accent = false }) => (
    <div style={{
      background: '#ffffff',
      border: `1px solid ${accent ? 'rgba(212,160,23,0.25)' : 'rgba(10,46,20,0.1)'}`,
      boxShadow: `6px 6px 0px 0px ${accent ? 'rgba(212,160,23,0.12)' : 'rgba(10,46,20,0.06)'}`,
      padding: isMobile ? '1.25rem' : '1.75rem',
      fontSize: 14, color: '#424841', lineHeight: 1.75,
      fontFamily: 'Manrope, sans-serif',
    }}>
      {children}
    </div>
  );

  const BulletList = ({ items }) => (
    <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: '#424841' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d4a017', marginTop: 7, flexShrink: 0, display: 'inline-block' }} />
          {item}
        </li>
      ))}
    </ul>
  );

  return (
    <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .terms-section { scroll-margin-top: 100px; }
        .nav-link-terms { transition: all 0.15s ease; }
        .nav-link-terms:hover { background: #f0eded !important; }
      `}</style>

      <AppNav
        activePage="terms"
        priceMode={priceMode}
        onModeToggle={onModeToggle}
        onNavigate={onNavigate}
        isMobile={isMobile}
        user={user}
        onUserChange={onUserChange}
      />
      <div style={{ height: navH }} />

      {/* ── Page Header — with farm image background ── */}
      <header style={{
        position: 'relative', overflow: 'hidden',
        padding: isMobile ? '3rem 1.25rem 3.5rem' : '4.5rem 2rem 5rem',
        marginBottom: isMobile ? '2rem' : '3rem',
        animation: 'fadeUp 0.5s ease both',
      }}>
        {/* Background image */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQRY0p2NwhoLFp6TO9XtXpqSryh7ombDAq4bA&s)',
          backgroundSize: 'cover', backgroundPosition: 'center top',
          zIndex: 0,
        }} />
        {/* Strong green overlay so text is clearly readable */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(0,24,5,0.82) 0%, rgba(10,46,20,0.75) 60%, rgba(0,24,5,0.80) 100%)',
          zIndex: 1,
        }} />
        {/* Gold gradient line at bottom */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(to right, #d4a017 0%, rgba(212,160,23,0.15) 50%, transparent 100%)', zIndex: 2 }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 3, maxWidth: 1280, margin: '0 auto' }}>
          <h1 style={{
            fontFamily: 'Epilogue, sans-serif',
            fontSize: isMobile ? 'clamp(1.75rem, 8vw, 2.5rem)' : 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', lineHeight: 1.05,
            marginBottom: 16,
          }}>
            Terms &amp;<br />Conditions
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, maxWidth: 600, marginBottom: 0, fontFamily: 'Manrope, sans-serif' }}>
            Last updated: <strong style={{ color: '#d4a017' }}>April 23, 2026.</strong> Please read these terms carefully before using the NaijaAgro Predict platform. By accessing our services, you agree to be bound by these terms.
          </p>
        </div>
      </header>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '0 1rem 3rem' : '0 2rem 5rem' }}>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: isMobile ? '1.5rem' : '3rem', alignItems: 'start' }}>

          {/* ── Sticky Sidebar ── */}
          {!isMobile && (
            <aside style={{ position: 'sticky', top: navH + 20 }}>
              <div style={{ marginBottom: 8, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#727970', fontFamily: 'Space Grotesk, sans-serif', padding: '0 12px' }}>
                Quick Navigation
              </div>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {SECTIONS.map(s => {
                  const isActive = activeSection === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className="nav-link-terms"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px',
                        background: isActive ? '#f0eded' : 'transparent',
                        border: 'none',
                        borderLeft: `3px solid ${isActive ? '#d4a017' : 'transparent'}`,
                        cursor: 'pointer', textAlign: 'left',
                        fontSize: 13, fontWeight: isActive ? 700 : 500,
                        color: isActive ? '#001805' : '#424841',
                        fontFamily: 'Manrope, sans-serif',
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ color: isActive ? '#d4a017' : '#c2c8bf', flexShrink: 0 }}>{s.icon}</span>
                      {s.label}
                    </button>
                  );
                })}
              </nav>

              {/* Support callout */}
              <div style={{
                marginTop: 20,
                background: '#0a2e14', color: '#fff',
                padding: '1.25rem', boxShadow: '6px 6px 0px 0px rgba(212,160,23,0.15)',
              }}>
                <div style={{ color: '#d4a017', marginBottom: 8 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </div>
                <p style={{ fontFamily: 'Epilogue, sans-serif', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Need clarification?</p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, marginBottom: 12 }}>
                  Our team is happy to help you understand our terms.
                </p>
                <a href="mailto:naijaagropredict@gmail.com" style={{
                  display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: '#d4a017', textDecoration: 'none',
                  fontFamily: 'Space Grotesk, sans-serif',
                  borderBottom: '1px solid rgba(212,160,23,0.4)',
                }}>
                  Contact Support →
                </a>
              </div>
            </aside>
          )}

          {/* ── Main Content ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '2rem' : '3rem' }}>

            {/* 1. Intellectual Property */}
            <section id="intellectual-property" className="terms-section">
              <SectionHeader num="1" title="Intellectual Property" icon={SECTIONS[0].icon} />
              <Card>
                <p>All intellectual property rights in and to the Service — including but not limited to software, databases, machine learning models, algorithms, design structure, user interface, and all related content — are owned by or licensed to the platform owner.</p>
                <p style={{ marginTop: 14 }}>You are granted a <strong>limited, revocable, non-exclusive, non-transferable license</strong> to access and use the Service strictly for its intended purpose.</p>
                <div style={{ marginTop: 16, padding: '14px 16px', background: '#f6f3f2', borderLeft: '3px solid #d4a017' }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#001805' }}>
                    You shall not copy, modify, distribute, sell, reverse engineer, or use the Service to create competing products without prior written consent.
                  </p>
                </div>
              </Card>
            </section>

            {/* 2. User Conduct */}
            <section id="user-conduct" className="terms-section">
              <SectionHeader num="2" title="User Conduct" icon={SECTIONS[1].icon} />
              <Card>
                <p>You agree to use the Service lawfully, responsibly, and in a manner consistent with all applicable local and national regulations. The following activities are strictly prohibited:</p>
                <BulletList items={[
                  'Submitting false, misleading, or fraudulent data to the platform',
                  'Attempting to manipulate or artificially influence predictions',
                  'Using bots, scrapers, or automated extraction tools without authorization',
                  'Interfering with or degrading system performance for other users',
                  'Attempting to gain unauthorized access to any part of the platform',
                  'Reverse-engineering any component of the prediction engine',
                ]} />
                <p style={{ marginTop: 14, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                  Violation of these terms may result in immediate suspension or permanent termination of access.
                </p>
              </Card>
            </section>

            {/* 3. Limitation of Liability — with background image */}
            <section id="limitation-of-liability" className="terms-section">
              <SectionHeader num="3" title="Limitation of Liability" icon={SECTIONS[2].icon} />
              <div style={{
                border: '1px solid rgba(10,46,20,0.1)',
                boxShadow: '6px 6px 0px 0px rgba(10,46,20,0.06)',
                overflow: 'hidden',
              }}>
                {/* Image banner at top of this card */}
                <div style={{
                  position: 'relative', height: isMobile ? 120 : 160, overflow: 'hidden',
                }}>
                  <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'url(https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQkQofRbCJc-jy6VxK9vGDP6q89vri6OmnU7A&s)',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                  }} />
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(135deg, rgba(10,46,20,0.80) 0%, rgba(26,71,49,0.65) 100%)',
                  }} />
                  <div style={{ position: 'relative', zIndex: 2, padding: isMobile ? '1.25rem' : '1.5rem 1.75rem', display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 600, fontFamily: 'Manrope, sans-serif', maxWidth: 560, lineHeight: 1.5 }}>
                      The platform provides data-driven price predictions for <span style={{ color: '#d4a017' }}>informational purposes only</span>. We do not guarantee the accuracy of any prediction or market insight.
                    </p>
                  </div>
                </div>

                {/* Card body */}
                <div style={{
                  background: '#ffffff',
                  padding: isMobile ? '1.25rem' : '1.75rem',
                  fontSize: 14, color: '#424841', lineHeight: 1.75,
                  fontFamily: 'Manrope, sans-serif',
                }}>
                  <p style={{ marginTop: 0 }}>NaijaAgro Predict, its directors, employees, partners, agents, and affiliates shall not be held liable for:</p>
                  <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                    {[
                      { title: 'Financial Loss', desc: 'Any loss arising from reliance on predictions or market data provided by the platform.' },
                      { title: 'Market Fluctuations', desc: 'Price changes caused by broader economic forces or events outside our data coverage.' },
                      { title: 'User-Submitted Data', desc: 'Errors or inaccuracies resulting from incorrect data submitted by users.' },
                      { title: 'Service Interruptions', desc: 'Downtime, data delays, or technical failures affecting platform availability.' },
                    ].map((item, i) => (
                      <div key={i} style={{ padding: '14px 16px', background: '#f6f3f2', border: '1px solid rgba(10,46,20,0.08)' }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#001805', marginBottom: 6 }}>{item.title}</div>
                        <div style={{ fontSize: 12, color: '#424841', lineHeight: 1.55 }}>{item.desc}</div>
                      </div>
                    ))}
                  </div>
                  <p style={{ marginTop: 16, fontSize: 13, fontWeight: 600, color: '#424841' }}>All use of the platform is at your own risk.</p>
                </div>
              </div>
            </section>

            {/* 4. Data Usage */}
            <section id="data-usage" className="terms-section">
              <SectionHeader num="4" title="Data Usage" icon={SECTIONS[3].icon} />
              <Card>
                <p>By using the platform, you acknowledge that data you submit may be collected and used to improve system performance, refine predictive models, and enhance the overall quality of the Service.</p>
                <p style={{ marginTop: 14 }}>All submitted data may be <strong>aggregated and anonymized</strong> for analytical purposes. No personally identifiable information will be sold or shared unlawfully with third parties.</p>
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', background: '#f0fdf4', border: '1px solid rgba(22,101,52,0.2)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <p style={{ margin: 0, fontSize: 13, color: '#166534', lineHeight: 1.55 }}>
                    Personal data, where applicable, is handled securely and in compliance with applicable data protection regulations.
                  </p>
                </div>
              </Card>
            </section>

            {/* 5. Governing Law */}
            <section id="governing-law" className="terms-section">
              <SectionHeader num="5" title="Governing Law" icon={SECTIONS[4].icon} />
              <Card>
                <p>These Terms and Conditions are governed by and construed in accordance with the <strong>laws of the Federal Republic of Nigeria</strong>.</p>
                <p style={{ marginTop: 14 }}>Any disputes arising from the use of this platform shall be subject to the exclusive jurisdiction of Nigerian courts. By using the Service, you irrevocably submit to this jurisdiction.</p>
              </Card>
            </section>

            {/* 6. Modifications */}
            <section id="modifications" className="terms-section">
              <SectionHeader num="6" title="Modifications to Terms" icon={SECTIONS[5].icon} />
              <Card>
                <p>We reserve the right to update or modify these Terms at any time at our sole discretion. When changes are made, the "Last Updated" date at the top of this page will be revised accordingly.</p>
                <p style={{ marginTop: 14 }}>Your continued use of the platform following the posting of updated Terms constitutes your acceptance of those changes. We encourage you to review this page periodically.</p>
              </Card>
            </section>

            {/* 7. Contact */}
            <section id="contact" className="terms-section">
              <SectionHeader num="7" title="Contact Information" icon={SECTIONS[6].icon} />
              <Card accent>
                <p style={{ marginBottom: 20 }}>For questions, clarifications, or concerns regarding these Terms and Conditions, please reach out to us through any of the following channels:</p>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                  <a href="mailto:naijaagropredict@gmail.com" style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', background: '#0a2e14', textDecoration: 'none',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    boxShadow: '4px 4px 0px 0px rgba(212,160,23,0.2)',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '6px 6px 0px 0px rgba(212,160,23,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '4px 4px 0px 0px rgba(212,160,23,0.2)'; }}
                  >
                    <div style={{ color: '#d4a017', flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 3 }}>Email</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#d4a017' }}>naijaagropredict@gmail.com</div>
                    </div>
                  </a>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', background: '#f6f3f2',
                    border: '1px solid rgba(10,46,20,0.12)',
                    boxShadow: '4px 4px 0px 0px rgba(10,46,20,0.06)',
                  }}>
                    <div style={{ color: '#0a2e14', flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.13 6.13l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#727970', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 3 }}>Phone</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#001805' }}>08069305651 · 07087917444</div>
                    </div>
                  </div>
                </div>
              </Card>
            </section>

          </div>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer style={{
        background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)',
        color: '#fff', padding: '1.75rem 2rem',
        borderTop: '3px solid #d4a017',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 12 }}>
          <div style={{ fontFamily: 'Epilogue, sans-serif', fontWeight: 800, fontSize: 15, color: '#d4a017' }}>NaijaAgro Predict</div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Home', nav: 'home' },
              { label: 'About Us', nav: 'about' },
              { label: 'Terms', nav: 'terms' },
              { label: 'Privacy Policy', nav: 'privacy' },
              { label: 'Contact', nav: 'about' },
            ].map(l => (
              <button key={l.label} onClick={() => onNavigate && onNavigate(l.nav)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, color: 'rgba(255,255,255,0.6)',
                fontFamily: 'Manrope, sans-serif', padding: 0,
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#d4a017'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
              >{l.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>© 2026 NaijaAgro Predict. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}